import * as React from "react"
import ITreatmentPlanTemplateResponseObj from "../models/ITreatmentPlanTemplateResponse";
import { CheckinType, ORION_IMG_URL } from "../utils/AppConstants";
import { isEmpty as _isEmpty, debounce as _debounce } from "lodash"
import { ModalComponent } from "./ModalComponent";
import TreatmentPlanReviewPdf from "./TreatmentPlanReviewPdf";
import { IAppState } from "../models/IAppState";
import { connect } from "react-redux";
import { IPdfTemplates } from "../models/PdfTemplatesModels";
import { ILabTest, IPrescriptionMedication, IFollowupInfo, IReferralInfo, IAdvice } from "../models/patients/IEHRSavePatientMedicalProfile"
import AppUtils from "../utils/AppUtils";

export interface ITreatmentPlanCardProps {
    listItem: ITreatmentPlanTemplateResponseObj,
    pdfTemplates: IPdfTemplates,
    onListItemClick?: (listItem: ITreatmentPlanTemplateResponseObj) => void
    onEditClick?: (listItem: ITreatmentPlanTemplateResponseObj) => void
    onDeleteClick?: (listItem: ITreatmentPlanTemplateResponseObj) => void
    editMode?: boolean;
    isAutoSuggested?: boolean;
    noOfTimesRepeated?: number
}

type Props = ReturnType<typeof mapStateToProps> & ITreatmentPlanCardProps

const TreatmentPlanCard: React.FC<Props> = ({
    listItem,
    pdfTemplates,
    onListItemClick = () => { },
    onEditClick = () => { },
    onDeleteClick = () => { },
    editMode = false,
    doctorProfile,
    staticSpecialities,
    isAutoSuggested,
    noOfTimesRepeated
}) => {

    const diagnosis = !_isEmpty(listItem.diagnosis)
        ? listItem.diagnosis
        : []
    const medicines = !_isEmpty(listItem.template.prescriptionMedicines)
        ? listItem.template.prescriptionMedicines.prescriptionMedicationList
        : []
    const labTests = !_isEmpty(listItem.template.prescriptionLabTest)
        ? listItem.template.prescriptionLabTest.labTestList
        : []
    const advice = !_isEmpty(listItem.template.prescriptionAdvice)
        ? listItem.template.prescriptionAdvice.advice
        : []
    const followUp = !_isEmpty(listItem.template.prescriptionInstructions)
        ? listItem.template.prescriptionInstructions.followupInfoSet
        : []
    const referral = !_isEmpty(listItem.template.prescriptionInstructions)
        ? listItem.template.prescriptionInstructions.referralInfoSet
        : []

    const [buttonClicked, setButtonClicked] = React.useState(false)
    const [showPdf, setShowPdf] = React.useState<boolean>(false)

    const debouncedCallback = _debounce((callback: (listItem: ITreatmentPlanTemplateResponseObj) => void) => {
        if (!buttonClicked) {
            callback(listItem)
            setButtonClicked(true)
        }
    }, 500, { leading: true, maxWait: 500, trailing: false })

    const renderAction = () => {
        if (editMode) {
            return (
                <>
                    <span className="action-items" onClick={() => debouncedCallback(onEditClick)}>
                        <img src={"/images/edit_image.svg"} height={16} width={16} />
                    </span>
                    <span className="action-items" onClick={() => debouncedCallback(onDeleteClick)}>
                        <img src={"/images/delete_image.svg"} height={16} width={16} />
                    </span>
                </>
            )
        }
        else {
            return (
                <span className="action-items" onClick={() => debouncedCallback(onListItemClick)}>
                    {"Select"}
                </span>
            )
        }
    }

    const parseFollowUp = (follow: IFollowupInfo) => {
        let checkinMapper = new Map<CheckinType, string>([
            [CheckinType.FOLLOWUP, "Follow up"],
            [CheckinType.REFERRAL, "Refer"]
        ])
        if (!_isEmpty(follow.checkinType) && !_isEmpty(follow.followUp) && follow.followUp.unit) {
            return (
                `${checkinMapper.get(follow.checkinType)} in ${AppUtils.isDefined(follow.followUp.count) ? follow.followUp.count : ""} ` +
                `${follow.followUp.unit.toLowerCase()}${follow.followUp.count && follow.followUp.count > 1 ? "s" : ""}`
            )
        } else {
            return ""
        }
    }
    const filterOutEmptyFollowps = (followups: IFollowupInfo[]) => {
        if (!followups || !followups.length) return [];
        return followups.filter(({ comment, followUp }) => comment || followUp);
    }
    return (
        <div>
            {isAutoSuggested &&
                <div className="template-auto-suggest-icon">
                    <span className="glyphicon glyphicon-link"></span>
                    <span>AUTO-SUGGESTED</span>
                </div>
            }
            {noOfTimesRepeated &&
                <div className="template-auto-suggest-icon">
                    <div className="red-dot"></div>
                    <span>{noOfTimesRepeated} Times</span>
                </div>
            }
            <span className="treatment-plan-item">
                <div className="treatment-plan-header-container col-xs-12">
                    <span className="treatment-plan-header col-xs-12">
                        <img src="/images/icons/treatmentPlanHeader.svg" height={19} width={25} />
                        {listItem.templateName}
                    </span>
                    <span className="treatment-plan-header-details">
                        {listItem.templateName}
                    </span>
                </div>
                <div className="left-column hide-scrollbar" onClick={() => setShowPdf(true)}>
                    {/* Diagnosis Section */}
                    {diagnosis && diagnosis.length > 0 &&
                        <div className="treatment-plan-section">
                            <span className="treatment-plan-subHeader">
                                {`DIAGNOSIS (${diagnosis.length})`}
                            </span>
                            {diagnosis.map((diag: string, index: number) => {
                                return (
                                    <span key={index} className="treatment-plan-content">{`${diag}`}</span>
                                )
                            })}
                        </div>
                    }
                    {/* Lab Tests Section */}
                    {labTests && labTests.length > 0 &&
                        <div className="treatment-plan-section">
                            <span className="treatment-plan-subHeader">
                                {`LAB TEST (${labTests.length})`}
                            </span>
                            {labTests.map((labTest: ILabTest, index: number) => {
                                return (
                                    <span key={index} className="treatment-plan-content">{`${labTest.diagnosis.displayValue}`}</span>
                                )
                            })}
                        </div>
                    }
                    {/* Medicine Section */}
                    {medicines && medicines.length > 0 &&
                        <div className="treatment-plan-section">
                            <span className="treatment-plan-subHeader">
                                {`MEDICINES (${medicines.length})`}
                            </span>
                            {medicines.map((medicine: IPrescriptionMedication, index: number) => {
                                return (
                                    <span key={index} className="treatment-plan-content">{`${medicine.brand.displayValue}`}</span>
                                )
                            })}
                        </div>
                    }
                    {/* Advice Section */}
                    {advice && advice.length > 0 &&
                        <div className="treatment-plan-section">
                            <span className="treatment-plan-subHeader">{`ADVICES (${advice.length})`}</span>
                            {advice.map((adv: IAdvice, index: number) => {
                                return (
                                    <span key={index} className="treatment-plan-content">{`${adv.doctorAdvice.displayValue}`}</span>
                                )
                            })}
                        </div>
                    }
                    {/* Follow Up Section */}
                    {followUp && filterOutEmptyFollowps(followUp).length > 0 &&
                        <div className="treatment-plan-section">
                            <span className="treatment-plan-subHeader">{`FOLLOW UP (${followUp.length})`}</span>
                            {followUp.map((follow: IFollowupInfo, index: number) => {
                                return (
                                    <span key={index} className="treatment-plan-content">{`${parseFollowUp(follow)}`}</span>
                                )
                            })}
                        </div>
                    }
                    {/* Referral Section */}
                    {referral && referral.length > 0 &&
                        <div className="treatment-plan-section">
                            <span className="treatment-plan-subHeader">{`REFERRAL (${referral.length})`}</span>
                            {referral.map((refer: IReferralInfo, index: number) => {
                                return (
                                    <span key={index} className="treatment-plan-content">{`${AppUtils.getSpecialityNameFromCode(refer.doctorSpecialityCode, staticSpecialities)}`}</span>
                                )
                            })}
                        </div>
                    }
                </div>
                <div className="right-column">
                    {buttonClicked
                        ? <img src={ORION_IMG_URL + "loader/rolling-loader.gif"} height={50} width={50} />
                        : renderAction()}
                </div>
                {Boolean(showPdf && !_isEmpty(pdfTemplates)) &&
                    <div className="ongoing-consultation-cont treatmentPlan">
                        <ModalComponent onClickCloseHandler={() => setShowPdf(false)} modalSize={"full"}>
                            <TreatmentPlanReviewPdf
                                previewMode={true}
                                templateData={listItem}
                                toggleModal={setShowPdf}
                                pdfTemplates={pdfTemplates}
                                staticSpecialistData={staticSpecialities}
                                doctorProfile={doctorProfile}
                            />
                        </ModalComponent>
                    </div>
                }
            </span>
        </div>
    );
}

const mapStateToProps = (state: IAppState) => {
    return {
        doctorProfile: state.doctorProfile,
        staticSpecialities: state.allStaticDataNew.doctorSpecialityResponseList,
    }
}

export default connect<ReturnType<typeof mapStateToProps>, null, ITreatmentPlanCardProps>(
    mapStateToProps
)(TreatmentPlanCard);